import { useState, useEffect } from 'react';
import CustomerMessageBox from './CustomerMessageBox.jsx';

// Confirmation on the Consignment action so the operator can set the seller's
// share — the % the customer receives of the final sale price — for THIS offer.
// Preloaded from the Rules-page default (or an existing negotiated split), the
// confirmed value is stamped onto the quote at offer time and frozen per item
// at listing time, so it defines the terms the customer accepts. One confirm
// for the whole quote (same reasoning as PairConfirmDialog / ConfirmOfferDialog).
//
// It also takes the optional message to the customer for the offer email.
// `loadPreview(splitPct)` fetches the email text for this quote; it must be a stable
// function (useCallback), or the preview reloads on every render.
export default function ConsignmentConfirmDialog({ itemName, itemCount = 1, defaultSplit, busy, loadPreview, toEmail, onCancel, onConfirm }) {
    const [share, setShare] = useState(defaultSplit != null ? String(defaultSplit) : '');
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');
    const [preview, setPreview] = useState(null);   // { standard, before, after } | { error }

    // The email states the seller share, so the preview reloads when a valid share is typed.
    const typedPct = Number(share);
    const validPct = Number.isInteger(typedPct) && typedPct >= 1 && typedPct <= 99 ? typedPct : null;
    useEffect(() => {
        if (!loadPreview || validPct == null) return undefined;
        let alive = true;
        setPreview(null);
        loadPreview(validPct)
            .then(p => { if (alive) setPreview(p); })
            .catch(() => { if (alive) setPreview({ error: true }); });
        return () => { alive = false; };
    }, [loadPreview, validPct]);

    // The share is quote-level — one split for the whole offer — so naming a
    // single item on a multi-item quote reads as "only this item". Name it when
    // there is one; otherwise summarise by count and say it covers them all.
    const label = itemCount > 1 ? `${itemCount} items` : itemName;

    function confirm() {
        const pct = Number(share);
        // Whole percent, 1–99: 0 or 100 is never a real Commission Service split,
        // and the backend/Prism reject out-of-range anyway — catch it here so the
        // operator sees why instead of a failed send.
        if (!Number.isInteger(pct) || pct < 1 || pct > 99) {
            setError('The seller share must be a whole number between 1 and 99.');
            return;
        }
        onConfirm(pct, message.trim());
    }

    return (
        <div className="modal-overlay">
            <div className="modal-content qa-pair-modal" style={{ width: '100%', maxWidth: 640 }} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Confirm consignment terms</h2>
                    <button className="modal-close" onClick={onCancel}>&times;</button>
                </div>
                <div className="modal-body-scroll">
                    <div className="qa-pair-explain">
                        <strong>Commission Service offer{label ? ` — ${label}` : ''}.</strong> Set the
                        share of the final sale price the customer receives. This is stamped onto the offer and
                        becomes the terms they accept, so check it before sending.
                        {itemCount > 1 && ' This share applies to every item on the quote.'}
                    </div>
                    <label className="qa-consign-share">
                        <span>Seller receives (% of final sale)</span>
                        <input type="number" min="1" max="99" step="1" className="qa-pair-qty-input"
                            value={share} onChange={e => setShare(e.target.value)} />
                    </label>
                    {error && <div className="qa-pair-error">{error}</div>}
                    {!toEmail && (
                        <div className="qa-error qa-banner" role="alert" style={{ marginTop: 'var(--space-4)' }}>
                            No email address was found for this customer. If you send now, the offer is recorded but no email is sent.
                        </div>
                    )}
                    <CustomerMessageBox id="consignment-message" value={message} onChange={setMessage}
                        preview={preview} toEmail={toEmail} testId="consignment-email-preview"
                        placeholder="Its own paragraph after the thank-you line. Write full sentences, as 'we'." />
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
                    <button className="btn btn-consignment" onClick={confirm} disabled={busy}>
                        {busy ? '…' : 'Send consignment offer'}
                    </button>
                </div>
            </div>
        </div>
    );
}
